import { Injectable, NgZone } from '@angular/core';
import { ModalController } from '@ionic/angular/standalone';
import { Router } from '@angular/router';

import { PushContent } from '../shared/interfaces/pushContent';
import { PushNotificationModalComponent, PushNotificationModalInput } from '../shared/modals/push-notification-modal/push-notification-modal.component';

@Injectable({ providedIn: 'root' })
export class PushContentService {

  private isModalOpen = false;
  private lastNotificationKey: string | null = null;

  constructor(
    private modalCtrl: ModalController,
    private router: Router,
    private zone: NgZone,
  ) {}

  async gestisciNotifichePush(pushContent: PushContent): Promise<void> {
    this.zone.run(async () => {
      const input = this.normalize(pushContent);

      const key = input.notificationId ?? this.computeFallbackKey(input);
      if (key && this.lastNotificationKey === key) return;
      this.lastNotificationKey = key;

      await this.openModal(input);
    });
  }

  private async openModal(input: PushNotificationModalInput): Promise<void> {
    if (this.isModalOpen) return;
    this.isModalOpen = true;

    const modal = await this.modalCtrl.create({
      component: PushNotificationModalComponent,
      componentProps: input,
      backdropDismiss: true,
      htmlAttributes: { 'aria-labelledby': 'modal-title' }
    });

    await modal.present();

    const result = await modal.onWillDismiss<{ action?: 'open'; route?: string }>();
    this.isModalOpen = false;

    if (result?.data?.action === 'open' && result.data.route) {
      await this.router.navigateByUrl(result.data.route);
    }
  }

  private normalize(pushContent: PushContent): PushNotificationModalInput {
    const title = (pushContent?.title ?? '').trim();
    const message = (pushContent?.message ?? '').trim();

    const extra = pushContent?.extraData ?? undefined;

    const notificationId = (extra?.notificationId ?? '').trim() || undefined;
    const notificationType = (extra?.notificationType ?? '').trim() || 'GENERIC';

    const bodyParsed = this.tryParseJson(extra?.body);
    const route = this.routeFor(notificationType, bodyParsed, extra);

    // UI fields
    const detailsLines: Array<{ label: string; value?: string }> = [
      { label: 'Type', value: notificationType },
      { label: 'ID', value: notificationId },
      { label: 'Progetto', value: extra?.progettoId },
      { label: 'Timestamp', value: extra?.timestamp },
    ].filter(x => !!x.value) as any;

    return {
      modalTitle: title || extra?.title || 'Notifica',
      modalMessage: message || undefined,
      notificationId,
      notificationType,
      progettoId: extra?.progettoId,
      timestamp: extra?.timestamp,
      bodyRaw: extra?.body,
      bodyParsed,
      route,
      detailsLines,
      raw: pushContent,
    };
  }

  private routeFor(notificationType: string, bodyParsed: any, extra?: PushContent['extraData']): string | null {
    switch (notificationType) {
      case 'PROGETTO':
      case 'PROJECT':
        return extra?.progettoId
          ? `/progetti/${encodeURIComponent(extra.progettoId)}`
          : (bodyParsed?.progettoId ? `/progetti/${encodeURIComponent(bodyParsed.progettoId)}` : null);

      case 'NOTIFICATION_DETAIL':
        return extra?.notificationId
          ? `/notifiche/${encodeURIComponent(extra.notificationId)}`
          : null;

      case 'NAVIGATE':
        if (typeof bodyParsed === 'string' && bodyParsed.startsWith('/')) return bodyParsed;
        if (bodyParsed?.route && typeof bodyParsed.route === 'string') return bodyParsed.route;
        return null;

      default:
        return null;
    }
  }

  private tryParseJson(value?: string): any | null {
    if (!value) return null;
    const v = value.trim();
    if (!v) return null;
    try {
      return JSON.parse(v);
    } catch {
      return null;
    }
  }

  private computeFallbackKey(input: PushNotificationModalInput): string | null {
    const s = `${input.notificationType}|${input.modalTitle}|${input.timestamp ?? ''}|${input.progettoId ?? ''}`;
    return s.length ? s : null;
  }
}
